import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { getShippingDiscounts } from '@/routes/integrations';
import { setBase64 } from '@/store/ducks/base/actions';
import { setValidated } from '@/store/ducks/order/actions';
import { IShopConfig } from '@/store/ducks/shopConfig/types';

import { validateOrder } from '../../routes/order';
import { getShopConfig } from '../../routes/settings';
import { baseIsValid, getSettingsByBase64 } from '../../services/order';
import { initiateSocket, subscribeToOrder } from '../../services/socket';
import { ApplicationState } from '../../store';
import { IOrder } from '../../store/ducks/order/types';
import * as PaymentActions from '../../store/ducks/payment/actions';
import { setSettings, setWebappEnable } from '../../store/ducks/settings/actions';
import * as SettingsActions from '../../store/ducks/settings/actions';
import { ISettingConfig } from '../../store/ducks/settings/types';
import { OrderContext } from '../components/contexts/OrderContext';
import { WebContext } from '../components/contexts/WebContext';
import DefaultSettingsRedirect from '../components/redirect/DefaultSettingsRedirect';
import TopAlertMessage from '../components/topAlertMessage/TopAlertMessage';
import Footer from '../components/Web/footer/Footer';
import Navbar from '../components/Web/navbar/Navbar';
import { setLocalOrder } from '../hooks/setLocalOrder';
import { useLocalOrder } from '../hooks/useLocalOrder';
import { usePageTrack } from '../hooks/usePageTrack';
import Booklet from '../pages/booklet/Booklet';
import Cart from '../pages/cart/Cart';
import Category from '../pages/category/Category';
import Disabled from '../pages/disabled/Disabled';
import Home from '../pages/home/Home';
import NotFound from '../pages/notFound/NotFound';
import PrivacyPolicy from '../pages/privacyPolicy/PrivacyPolicy';
import Product from '../pages/product/Product';
import Search from '../pages/search/Search';
import World from '../pages/world/World';

const AskCpfModal = lazy(() => import('../components/askCpfModal/AskCpfModal'));
const CartAlertModal = lazy(() => import('../components/cartAlertModal/CartAlertModal'));
const IdentificationModal = lazy(
  () => import('../components/identificationModal/IdentificationModal'),
);
const OrderIsAlreadyWithSellerModal = lazy(
  () => import('../components/orderISAlredyWithSellerModal/OrderIsAlreadyWithSellerModal'),
);

function Web() {
  const dispatch = useDispatch();
  const history = useHistory();

  const configs = useMemo(() => JSON.parse(window.configs), []);

  const [validateOrderCount, setValidateOrderCount] = useState<number>(0);
  const validationInProgress = useRef<boolean>(false);
  const [showCartAlertModal, setShowCartAlertModal] = useState<boolean>(false);

  const [showCPFModal, setShowCPFModal] = useState<boolean>(false);
  const [showIdenfificationModal, setShowIdenfificationModal] = useState<boolean>(false);
  const [showIsCartBlockedModal, setShowIsCartBlockedModal] = useState<boolean>(false);

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [combosSerials, setCombosSerials] = useState<string[]>([]);
  const [orderFunctions, setOrderFunctions] = useState<Record<string, (...args: any[]) => any>>({});

  const localStorageWOD = JSON.parse(localStorage.getItem('webapp_out_of_delivery') || 'false');
  const localStorageMOA = JSON.parse(localStorage.getItem('min_order_amount') || '0');

  const [minOrderAmount, setMinOrderAmountState] = useState<number>(localStorageMOA);
  const [webappOutOfDelivery, setWebappOutOfDeliveryState] = useState<boolean>(localStorageWOD);

  const setMinOrderAmount = useCallback((amount: number) => {
    setMinOrderAmountState(amount);
    localStorage.setItem('min_order_amount', JSON.stringify(amount));
  }, []);

  const setWebappOutOfDelivery = useCallback((outOfDelivery: boolean) => {
    setWebappOutOfDeliveryState(outOfDelivery);
    localStorage.setItem('webapp_out_of_delivery', JSON.stringify(outOfDelivery));
  }, []);

  // Dynamic functions management
  const setOrderFunction = useCallback((key: string, fn: (...args: any[]) => any) => {
    setOrderFunctions((prev) => ({ ...prev, [key]: fn }));
  }, []);

  const useOrderFunction = useCallback(
    <T extends (...args: any[]) => any>(key: string): T | undefined => {
      return orderFunctions[key] as T | undefined;
    },
    [orderFunctions],
  );

  const removeOrderFunction = useCallback((key: string) => {
    setOrderFunctions((prev) => {
      const newFunctions = { ...prev };
      delete newFunctions[key];
      return newFunctions;
    });
  }, []);

  const selectBase64 = useSelector<ApplicationState, string>((state) => state.base.data);
  const selectOrder = useSelector<ApplicationState, IOrder | null>((state) => state.order.data);
  const selectConfigs = useSelector<ApplicationState, ISettingConfig>(
    (state) => state.settings.configs,
  );
  const selectOrderIsValidated = useSelector<ApplicationState, boolean>(
    (state) => state.order.validated,
  );
  const selectShopIdDst = useSelector<ApplicationState, number | null>(
    (state) => state.order.data?.dst_shop_id ?? null,
  );
  const selectConsentGiven = useSelector<ApplicationState, boolean>(
    (state) => state.privacyPolicy.consentGiven,
  );
  const selectPrivacyPolicy = useSelector<ApplicationState, string | null>(
    (state) => state.privacyPolicy.data.privacy_policy,
  );
  const selectTokenValidated = useSelector<ApplicationState, boolean | null>(
    (state) => state.token.tokenValidated,
  );

  const selectShop = useSelector<ApplicationState, IShopConfig>((state) => state.shopConfig.data);
  const selectDefaultShop = useSelector((state: ApplicationState) => state.defaultShop.address);
  const isValidBase64 = (str: string) => {
    try {
      return Number.isNaN(parseInt(str)) && btoa(atob(str)) === str;
    } catch (e) {
      return false;
    }
  };

  const myValidateBase64 = (str: string) => {
    if (typeof str !== 'string') return false;

    try {
      const decoded = atob(str);
      const encoded = btoa(decoded);

      return decoded.includes(';') && encoded === str;
    } catch (e) {
      return false;
    }
  };

  const [localStorageOrder, setLocalStorageOrder] = useState<string | IOrder | false>(() => {
    return useLocalOrder();
  });

  useEffect(() => {
    const handleStorageChange = () => {
      setLocalStorageOrder(useLocalOrder());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const getUrlBase64 = useCallback(() => {
    const localOrder = localStorageOrder;

    let localOrderBase64 = '';
    if (
      typeof localOrder === 'object' &&
      localOrder !== null &&
      'id' in localOrder &&
      'company_id' in localOrder &&
      'dst_shop_id' in localOrder
    ) {
      localOrderBase64 = btoa(
        `${localOrder.id};${localOrder.company_id};${localOrder.dst_shop_id}`,
      );
    } else if (typeof localOrder === 'string' && localOrder.length > 0) {
      localOrderBase64 = localOrder;
    }

    const hasMultipleBase64 = history.location.pathname
      .split('/')
      .filter((s) => myValidateBase64(s));

    const urlBase64 = localOrderBase64 || hasMultipleBase64[0] || '';

    return { urlBase64 };
  }, [localStorageOrder, history.location.pathname]);

  const loadGoogleMapsScript = () => {
    const scriptId = 'google-maps-script';
    if (document.getElementById(scriptId)) return;
    const appUrl = process.env.REACT_APP_BV_URL || '';
    const cookies = new Cookies();
    const token = cookies.get('token');
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `${appUrl}/api/v1/download-action/google-maps?token=${token}`;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';

    document.body.appendChild(script);
  };

  const setConfigs = useCallback(async () => {
    if (!window.configs) return;
    const configs = JSON.parse(window.configs) as ISettingConfig;
    dispatch(SettingsActions.setConfigs(configs as ISettingConfig));
  }, [dispatch]);

  const removeBase64FromUrl = useCallback(() => {
    const params = new URLSearchParams(history.location.search);
    const segments = history.location.pathname.split('/');
    const lastSegment = segments[segments.length - 1] || '';
    const isBase64param = isValidBase64(lastSegment);

    if (isBase64param) {
      segments.pop();
      const url = segments.join('/');
      history.replace(`${url}?${params.toString()}`);
      return true;
    }
    return false;
  }, [history]);

  /**
   * @summary restarting PaidAlert state.
   */
  useEffect(() => {
    dispatch(PaymentActions.setShowPaidAlert(false));
  }, [selectBase64]);

  /**
   * @summary validating base64.
   */
  useEffect(() => {
    const { urlBase64 } = getUrlBase64();

    if (urlBase64 && urlBase64 !== selectBase64) {
      const settings = getSettingsByBase64(urlBase64);

      if (baseIsValid(settings)) {
        dispatch(setBase64(urlBase64));
        dispatch(setSettings(settings));
        dispatch(PaymentActions.setHasShownPaidAlert(false));
      }
    }
  }, []);

  useEffect(() => {
    removeBase64FromUrl();
    setConfigs();
  }, [removeBase64FromUrl, setConfigs]);

  /**
   * @summary validating order.
   * @description validate order IF has the base64 from storage AND the base64 is valid. Maximum 2 runs (initial + auth retry).
   */
  useEffect(() => {
    if (validateOrderCount >= 2) {
      return;
    }

    if (validationInProgress.current) {
      return;
    }

    if (selectOrderIsValidated && selectBase64) {
      return;
    }

    if (validateOrderCount >= 1 && !selectTokenValidated) {
      return;
    }

    const { urlBase64 } = getUrlBase64();
    const defaultBase64 = ['null', configs.company_id, configs?.default_shop ?? ''].join(';');
    const base =
      urlBase64 && urlBase64.length && baseIsValid(getSettingsByBase64(urlBase64))
        ? urlBase64
        : btoa(defaultBase64);

    const { orderId, shopId } = getSettingsByBase64(base);
    const shopToUse = selectShop.id && selectShop.id > 0 ? selectShop.id : shopId;

    if (!shopToUse) {
      return;
    }

    validationInProgress.current = true;
    setValidateOrderCount((old) => old + 1);

    validateOrder(shopToUse, orderId)
      .then((order) => {
        validationInProgress.current = false;

        if (!order) {
          return;
        }

        const base64 = btoa(`${order.id};${order.company_id};${order.shop_id}`);

        if (base64 !== selectBase64) {
          setLocalOrder(order);
          dispatch(setBase64(base64));
          dispatch(setSettings(getSettingsByBase64(base)));
        }

        initiateSocket();
        getShippingDiscounts(order.dst_shop_id);
        dispatch(setValidated(true));
        subscribeToOrder(order.id);
        loadGoogleMapsScript();

        if (!selectDefaultShop.id) {
          getShopConfig(order.shop_id, true);
        }

        setValidateOrderCount(2);
      })
      .catch((e) => {
        validationInProgress.current = false;

        if (e.response?.status === 401 || e.response?.status === 403) {
          return;
        }

        if (e.response?.status === 422) dispatch(setWebappEnable(false));
        setValidateOrderCount(2);
      });
  }, [selectTokenValidated]);

  useEffect(() => {
    if (selectShopIdDst) getShopConfig(selectShopIdDst);
  }, [selectShopIdDst]);

  usePageTrack();

  return (
    <>
      {!selectConfigs.is_enabled ? (
        <Disabled />
      ) : (
        <>
          {selectBase64 === '' ? (
            <DefaultSettingsRedirect />
          ) : (
            <>
              <OrderContext.Provider
                value={{
                  order: selectOrder,
                  combosSerials,
                  setCombosSerials,
                  shipment: selectOrder?.shipment || null,
                  payments: selectOrder?.payments || null,
                  functions: orderFunctions,
                  setFunction: setOrderFunction,
                  useFunction: useOrderFunction,
                  removeFunction: removeOrderFunction,
                }}
              >
                <WebContext.Provider
                  value={{
                    selectBase64,
                    order: selectOrder,
                    configs: selectConfigs,
                    consentGiven: selectConsentGiven,
                    menuOpen,
                    privacyPolicy: selectPrivacyPolicy,
                    shop: selectShop,
                    defaultShop: selectDefaultShop,
                    min_order_amount: minOrderAmount,
                    webapp_out_of_delivery: webappOutOfDelivery,
                    setShowCartAlertModal,
                    setMenuOpen,
                    setShowIsCartBlockedModal,
                    setShowCPFModal,
                    setShowIdenfificationModal,
                    setWebappOutOfDelivery,
                    setMinOrderAmount,
                  }}
                >
                  <header className="sticky-top">
                    <TopAlertMessage />
                    <Navbar />
                  </header>

                  <main style={{ backgroundColor: selectConfigs.bg_color }}>
                    <Switch>
                      <Route path="/" exact component={DefaultSettingsRedirect} />

                      <Route path="/w" exact component={Home} />
                      <Route path="/w/:base64?" component={Home} />

                      <Route path="/search" exact component={Search} />
                      <Route path="/search/:base64?" component={Search} />

                      <Route path="/cart" exact component={Cart} />
                      <Route path="/cart/:base64" component={Cart} />

                      <Route path="/product/:productId" exact component={Product} />
                      <Route path="/product/:productId/:name?/:base64?" component={Product} />

                      <Route path="/group/:id" exact component={Category} />
                      <Route path="/group/:id/:names?/:base64?" component={Category} />

                      <Route path="/category/:id" exact component={Category} />
                      <Route path="/category/:id?/:names?/:base64?" component={Category} />

                      <Route path="/cw/:id" exact component={World} />
                      <Route path="/cw/:id/:name" exact component={World} />
                      <Route path="/cw/:id/:name/:base64" component={World} />

                      <Route path="/booklets" exact component={Booklet} />
                      <Route path="/booklets/:base64" component={Booklet} />

                      <Route path="/privacy-policy" exact component={PrivacyPolicy} />
                      <Route path="/privacy-policy/:base64?" component={PrivacyPolicy} />

                      <Route path="*" component={NotFound} />
                    </Switch>
                  </main>

                  <Footer configs={selectConfigs} />

                  <Suspense fallback={<div />}>
                    <OrderIsAlreadyWithSellerModal
                      show={showIsCartBlockedModal}
                      setShow={setShowIsCartBlockedModal}
                      whatsappNumber={selectShop.whatsapp}
                    />

                    <CartAlertModal show={showCartAlertModal} setShow={setShowCartAlertModal} />
                    <AskCpfModal show={showCPFModal} setShow={setShowCPFModal} />
                    <IdentificationModal
                      show={showIdenfificationModal}
                      setShow={setShowIdenfificationModal}
                    />
                  </Suspense>
                </WebContext.Provider>
              </OrderContext.Provider>
            </>
          )}
        </>
      )}
    </>
  );
}

export default Web;
